import { useState, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import { Button } from "reactstrap"

const FullscreenWrapper = ({
  children,
  shortcut = "Ctrl+Shift+F",
  buttonPosition = "top-right", // top-right | top-left
  buttonColor = "primary",
  zIndex = 9999,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false)

  const toggle = useCallback(() => {
    setIsFullscreen(prev => !prev)
  }, [])

  // Keyboard Shortcut (Ctrl + Shift + F)
  useEffect(() => {
    const handleKeyDown = e => {
      if (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === "f") {
        e.preventDefault()
        toggle()
      }

      if (e.key === "Escape") {
        setIsFullscreen(false)
      }
    }

    document.addEventListener("keydown", handleKeyDown)

    return () => {
      document.removeEventListener("keydown", handleKeyDown)
    }
  }, [toggle])

  // Lock scroll when fullscreen
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
  }, [isFullscreen])

  // Corner position
  const getButtonPositionStyle = () => {
    const base = {
      position: "absolute",
      zIndex: 20,
    }

    if (buttonPosition === "top-left") {
      return { ...base, top: 24, left: 24 }
    }

    return { ...base, top: 24, right: 24 }
  }

  const wrapperStyle = {
    position: "relative",
  }

  const fullscreenStyle = {
    position: "fixed",
    inset: 0,
    background: "#ffffff",
    zIndex,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
  }

  const renderButton = (styleOverride = {}) => (
    <Button
      color={buttonColor}
      size="sm"
      onClick={toggle}
      style={{ ...getButtonPositionStyle(), ...styleOverride }}
      title={`Toggle Fullscreen (${shortcut})`}
    >
      <i
        className={`mdi ${
          isFullscreen ? "mdi-fullscreen-exit" : "mdi-fullscreen"
        }`}
      />
    </Button>
  )

  // Normal Mode
  if (!isFullscreen) {
    return (
      <div style={wrapperStyle}>
        {renderButton()}
        {children}
      </div>
    )
  }

  // Fullscreen Mode (Portal)
  return createPortal(
    <div style={fullscreenStyle}>
      {renderButton({ position: "fixed" })}
      {children}
    </div>,
    document.body
  )
}

export default FullscreenWrapper
